/**
 * @file assets/theme-formatters.js
 * @description Shared formatters: product-detail values (weight, dimensions, unit
 *   aliases, detail rows) used by product-form.js and product-card.js, money
 *   formatting from shop.money_format (data-money-format on the cart root and the product
 *   price) used by cart-component.js and product-form.js, and the whole-number quantity
 *   parser (parseWholeNumber) shared by the same two files. Loaded first by
 *   layout/theme.liquid on every template; exposes window.ThemeFormatters only.
 */
(function () {
  'use strict';

  const LENGTH_UNIT_ALIASES = {
    millimeter: 'mm',
    millimeters: 'mm',
    millimetre: 'mm',
    millimetres: 'mm',
    centimeter: 'cm',
    centimeters: 'cm',
    centimetre: 'cm',
    centimetres: 'cm',
    meter: 'm',
    meters: 'm',
    metre: 'm',
    metres: 'm',
    inch: 'in',
    inches: 'in',
    foot: 'ft',
    feet: 'ft',
    yard: 'yd',
    yards: 'yd'
  };

  const WEIGHT_UNIT_ALIASES = {
    kilogram: 'kg',
    kilograms: 'kg',
    kilogramme: 'kg',
    kilogrammes: 'kg',
    gram: 'g',
    grams: 'g',
    gramme: 'g',
    grammes: 'g',
    pound: 'lb',
    pounds: 'lb',
    lbs: 'lb',
    ounce: 'oz',
    ounces: 'oz'
  };

  function normalizeDetailValue(value) {
    return String(value || '').trim();
  }

  // Shared weight rule, mirrored by the weight normalisation in snippets/variant-details.liquid
  // (P-4): a trailing "<number> <unit>" whose number is digits with at most one "." or ","
  // between digits gets a decimal point, no trailing zeros and the abbreviated lower-case unit
  // (650.0 g → 650 g, 0.0 kg → 0 kg, 1,5 kg → 1.5 kg, 900g → 900 g, 2 lbs → 2 lb); any other
  // text is returned as written. Change the two together.
  function formatWeight(value) {
    const normalizedValue = normalizeDetailValue(value);

    if (!normalizedValue) {
      return '';
    }

    return normalizedValue.replace(/㎏/g, 'kg').replace(/(-?\d+(?:[.,]\d+)?)\s*(kilogrammes?|kilograms?|grammes?|grams?|pounds?|ounces?|kg|g|lbs?|oz)$/i, (match, amount, unit) => {
      const normalizedUnit = String(unit || '').trim().toLowerCase();
      // A whole-number weight drops its trailing ".0", as the dimensions do (L-24).
      const normalizedAmount = String(Number(String(amount).replace(',', '.')));
      return `${normalizedAmount} ${WEIGHT_UNIT_ALIASES[normalizedUnit] || normalizedUnit}`;
    });
  }

  function extractDimensionValueAndUnit(value) {
    const normalizedValue = normalizeDetailValue(value);

    if (!normalizedValue) {
      return { value: '', unit: '' };
    }

    const dimensionMatch = normalizedValue.match(/^(.+?)\s*(millimeters?|millimetres?|centimeters?|centimetres?|meters?|metres?|inches?|inch|feet|foot|yards?|mm|cm|m|in|ft|yd)$/i);

    if (!dimensionMatch) {
      return { value: normalizedValue, unit: '' };
    }

    const normalizedUnit = dimensionMatch[2].trim().toLowerCase();

    return {
      value: dimensionMatch[1].trim(),
      unit: LENGTH_UNIT_ALIASES[normalizedUnit] || normalizedUnit
    };
  }

  function formatDimensionValue(value) {
    const normalizedValue = normalizeDetailValue(value);

    if (!normalizedValue) {
      return '';
    }

    const normalizedNumberValue = normalizedValue.replace(',', '.');

    if (/^-?\d+(?:\.\d+)?$/.test(normalizedNumberValue)) {
      // Keep decimals as entered; the server prints dimensions unrounded (CHANGELOG 2026-09-02).
      return String(Number(normalizedNumberValue));
    }

    return normalizedValue;
  }

  function normalizeDimensionUnit(value) {
    const normalizedValue = normalizeDetailValue(value).toLowerCase();

    if (!normalizedValue) {
      return '';
    }

    return LENGTH_UNIT_ALIASES[normalizedValue] || normalizedValue;
  }

  function formatDimensions(details) {
    if (!details || typeof details !== 'object') {
      return '';
    }

    const widthDetails = extractDimensionValueAndUnit(details.width);
    const heightDetails = extractDimensionValueAndUnit(details.height);
    const depthDetails = extractDimensionValueAndUnit(details.depth);
    const unit = normalizeDimensionUnit(details.unit) || widthDetails.unit || heightDetails.unit || depthDetails.unit;
    const parts = [];

    if (widthDetails.value) {
      parts.push(`W ${formatDimensionValue(widthDetails.value)}`);
    }

    if (heightDetails.value) {
      parts.push(`H ${formatDimensionValue(heightDetails.value)}`);
    }

    if (depthDetails.value) {
      parts.push(`D ${formatDimensionValue(depthDetails.value)}`);
    }

    if (!parts.length) {
      return '';
    }

    return unit ? `${parts.join(' × ')} ${unit}` : parts.join(' × ');
  }

  /**
   * Writes a variant detail into a rendered details list. Finds
   * [data-product-detail-row="rowName"] inside root, formats the value
   * (weight through formatWeight, everything else through normalizeDetailValue),
   * writes it into [data-product-detail-value="rowName"] and hides the row
   * when the value is empty. Shared by product-form.js and product-card.js.
   */
  function applyDetailRow(root, rowName, value) {
    if (!root) {
      return;
    }

    const detailRow = root.querySelector(`[data-product-detail-row="${rowName}"]`);

    if (!detailRow) {
      return;
    }

    const normalizedValue = rowName === 'weight'
      ? formatWeight(value)
      : normalizeDetailValue(value);
    const detailValue = detailRow.querySelector(`[data-product-detail-value="${rowName}"]`);

    if (detailValue) {
      detailValue.textContent = normalizedValue;
    }

    detailRow.hidden = !normalizedValue;
  }

  /**
   * Money format string (shop.money_format, DECISIONS O-8): the data-money-format
   * attribute on `element` or its closest ancestor that carries one, otherwise the
   * first data-money-format in the document. Returns '' when none is rendered.
   */
  function getMoneyFormat(element) {
    const closestSource = element instanceof Element
      ? element.closest('[data-money-format]')
      : null;
    const formatSource = closestSource || document.querySelector('[data-money-format]');

    return formatSource ? String(formatSource.getAttribute('data-money-format') || '').trim() : '';
  }

  function formatMoneyWithDelimiters(cents, precision, thousandsSeparator, decimalSeparator) {
    const fixed = (cents / 100).toFixed(precision);
    const [integerPart, decimalPart] = fixed.split('.');
    const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    return decimalPart ? `${groupedInteger}${decimalSeparator}${decimalPart}` : groupedInteger;
  }

  function formatMoneyPlaceholder(placeholderName, amount) {
    switch (placeholderName) {
      case 'amount_no_decimals':
        return formatMoneyWithDelimiters(amount, 0, ',', '.');
      case 'amount_with_comma_separator':
        return formatMoneyWithDelimiters(amount, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatMoneyWithDelimiters(amount, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return formatMoneyWithDelimiters(amount, 2, "'", '.');
      case 'amount_with_space_separator':
        return formatMoneyWithDelimiters(amount, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return formatMoneyWithDelimiters(amount, 0, ' ', '.');
      case 'amount_with_period_and_space_separator':
        return formatMoneyWithDelimiters(amount, 2, ' ', '.');
      case 'amount':
      default:
        return formatMoneyWithDelimiters(amount, 2, ',', '.');
    }
  }

  /**
   * Formats an amount in cents with a Shopify money format string, expanding the
   * {{ amount* }} placeholders. `format` defaults to getMoneyFormat(); when no
   * format is available at all the amount renders with plain {{amount}} semantics.
   */
  function formatMoney(cents, format) {
    const amount = Number(cents) || 0;
    const moneyFormat = (typeof format === 'string' && format.trim() ? format : getMoneyFormat()) || '{{amount}}';

    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholderName) => {
      return formatMoneyPlaceholder(placeholderName, amount);
    });
  }

  /**
   * Parses a quantity field: only a non-negative whole number (after trimming) is
   * accepted and returned as a Number; anything else (null, undefined, blank, NaN,
   * negative, decimals) is null so the caller can treat it as "no change". Shared by
   * cart-component.js and product-form.js.
   */
  function parseWholeNumber(value) {
    const normalizedValue = String(value === null || typeof value === 'undefined' ? '' : value).trim();

    if (!/^\d+$/.test(normalizedValue)) {
      return null;
    }

    return Number(normalizedValue);
  }

  window.ThemeFormatters = {
    normalizeDetailValue,
    formatWeight,
    formatDimensions,
    applyDetailRow,
    getMoneyFormat,
    formatMoney,
    parseWholeNumber
  };
})();
